import { Row } from '@tanstack/react-table';
import { LuChevronDown, LuChevronRight, LuCircleAlert } from 'react-icons/lu';

import { ApprovalStatus } from '@ndvr/billing-api';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EllipsisTooltip } from '@/components/ui/EllipsisTooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { AmendmentBadge } from './AmendmentBadge';
import {
    accountLabel,
    BillingRunTableRow,
    FEE_TYPE_BADGE_CLASSNAME,
    FEE_TYPE_BADGE_LABEL,
    primaryLabel,
    runFailed,
} from './billing-run-table';

type BillingRunNameCellProps = {
    row: Row<BillingRunTableRow>;
    accountNameByGUID: Map<string, string>;
    isUpcomingBillingDate: boolean;
};

export const BillingRunNameCell = ({ row, accountNameByGUID, isUpcomingBillingDate }: BillingRunNameCellProps) => {
    const item = row.original;
    const amended =
        item.kind === 'group' ? item.run.amended : item.kind === 'account' ? item.fee.amended : item.detail.amended;

    return (
        <div className="flex items-center gap-2" style={{ paddingLeft: `${row.depth * 16}px` }}>
            {row.getCanExpand() ? (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    aria-label={row.getIsExpanded() ? 'Collapse' : 'Expand'}
                    onClick={row.getToggleExpandedHandler()}
                >
                    {row.getIsExpanded() ? (
                        <LuChevronDown className="h-4 w-4" />
                    ) : (
                        <LuChevronRight className="h-4 w-4" />
                    )}
                </Button>
            ) : (
                <span className="w-7 shrink-0" />
            )}
            {item.kind === 'detail' && (
                <Badge variant="outline" className={FEE_TYPE_BADGE_CLASSNAME[item.detail.feeType]}>
                    {FEE_TYPE_BADGE_LABEL[item.detail.feeType]}
                </Badge>
            )}
            <div className="min-w-0 flex-1 overflow-hidden">
                <div className="flex min-w-0 items-center gap-1.5 font-medium">
                    <EllipsisTooltip className="min-w-0 flex-1" observeResize>
                        {primaryLabel(item)}
                    </EllipsisTooltip>
                    {amended && <AmendmentBadge />}
                    {item.kind === 'group' && item.run.approvalStatus === ApprovalStatus.APPROVED && (
                        <Badge
                            variant="outline"
                            className="shrink-0 border-transparent bg-green-100 px-1.5 py-0 text-[10px] leading-4 text-green-800"
                        >
                            Approved
                        </Badge>
                    )}
                    {item.kind === 'group' && runFailed(item) && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span>
                                    <Badge
                                        variant="outline"
                                        className="gap-1 border-transparent bg-red-100 px-1.5 py-0 text-[10px] leading-4 text-red-800"
                                    >
                                        <LuCircleAlert className="h-2.5 w-2.5" />
                                        {isUpcomingBillingDate ? 'Unavailable' : 'Failed'}
                                    </Badge>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                {isUpcomingBillingDate
                                    ? 'Fee estimation failed for this billing group'
                                    : 'Fee calculation failed for this billing run'}
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>
                {item.kind !== 'detail' && (
                    <EllipsisTooltip className="text-muted-foreground text-xs" observeResize>
                        {item.kind === 'group'
                            ? [
                                  item.run.feeScheduleName,
                                  !isUpcomingBillingDate || !runFailed(item)
                                      ? accountLabel(item.run.accountFees.length)
                                      : undefined,
                              ]
                                  .filter(Boolean)
                                  .join(' · ')
                            : item.fee.settlementAccountGUID === item.fee.accountGUID
                              ? 'Default settlement account'
                              : `Settles from ${accountNameByGUID.get(item.fee.settlementAccountGUID) ?? item.fee.settlementAccountGUID}`}
                    </EllipsisTooltip>
                )}
            </div>
        </div>
    );
};
